/**
 * `monetization/purchase` — **the PLATFORM clamp for the purchase (client side).**
 *
 * EXACT mirror of `packages/core-rust/src/monetization/purchase.rs`. The two
 * halves have to say the same thing, and that's why the clamp doesn't live in the screen:
 * it lives here, in the kit, where it can be tested without a DOM and where a clone
 * copies it whole.
 *
 * ## What fails if this doesn't exist
 *
 * Three rows in the admin panel (`monetization.enabled`, `payments.enabled`,
 * `payments.provider=stripe`) make an **already-installed APK** render "Upgrade"
 * and run `location.assign(checkoutUrl)` toward Stripe. Selling digital content
 * inside a binary distributed by Google Play without Play Billing is
 * **grounds for takedown**, and the switch reaches the binary **without
 * redeploying and without reuploading**.
 *
 * ## Why BOTH layers are needed
 *
 * The server no longer returns a checkout URL to a store binary
 * (`billing_checkout_handler`). Even so, the client decides again, for two
 * reasons that are not redundancy:
 *
 *   1. **The button.** An "Upgrade" that always fails is a broken screen; worse,
 *      it's a purchase surface visible inside the binary, which is
 *      exactly what Play policy looks at.
 *   2. **The already-installed binary doesn't update when the server changes.**
 *      An old APK talking to a new server is protected by the server;
 *      a new APK talking to an old server — or one from a different
 *      installation — protects itself. Only with both, is the published
 *      binary safe no matter what happens on the other side.
 *
 * ## What it is NOT
 *
 * It's not the Play billing layer: there is no `play` provider, no native purchase,
 * no webhooks. That's a separate decision (branch `rescue/play-billing-950a8e5b`).
 * Here we only prevent selling where selling is illegal.
 */

import { getPlatformOS, type PlatformOS } from "@fleet/utils";

/** Purchase surfaces the portfolio distinguishes. */
export const PURCHASE_PLATFORMS = ["web", "desktop", "android", "ios"] as const;

export type PurchasePlatform = (typeof PURCHASE_PLATFORMS)[number];

/** Non-classifiable surface ⇒ fail-closed. */
export const UNKNOWN_PURCHASE_PLATFORM = "unknown";

export type ResolvedPurchasePlatform = PurchasePlatform | typeof UNKNOWN_PURCHASE_PLATFORM;

/** "No gateway" provider: the upgrade is applied by a human. Always legal. */
export const PAYMENT_PROVIDER_NONE = "none";

/** Provider ids the kit knows how to build (mirror of the Rust). */
export const KNOWN_PAYMENT_PROVIDERS: readonly string[] = ["none", "stripe"];

export function isPurchasePlatform(value: string): value is PurchasePlatform {
  return (PURCHASE_PLATFORMS as readonly string[]).includes(value);
}

/**
 * **NON-NEGOTIABLE.** Which providers are LEGAL on each surface.
 *
 * - `web` / `desktop` — Stripe is legitimate: neither one has the
 *   Play/App Store obligation (nor Microsoft Store).
 * - `android` — either Play Billing or nothing, and the Play provider doesn't exist in this
 *   kit ⇒ today only `none`.
 * - `ios` — only `none`: no Apple IAP provider.
 *
 * No configuration path can add an entry to this table: it isn't
 * read from config, it's read from here.
 */
export const PLATFORM_ALLOWED_PAYMENT_PROVIDERS: Readonly<
  Record<PurchasePlatform, readonly string[]>
> = {
  web: [PAYMENT_PROVIDER_NONE, "stripe"],
  desktop: [PAYMENT_PROVIDER_NONE, "stripe"],
  android: [PAYMENT_PROVIDER_NONE],
  ios: [PAYMENT_PROVIDER_NONE],
};

/** Floor for an unclassified surface: only the manual provider. */
export const UNKNOWN_PLATFORM_ALLOWED_PROVIDERS: readonly string[] = [PAYMENT_PROVIDER_NONE];

/**
 * Surfaces that are a **store binary**: where payment policy bites.
 * `desktop` is NOT included: it's a binary, but no store reviews it.
 */
export const STORE_BINARY_PLATFORMS: readonly string[] = ["android", "ios"];

export function isStoreBinaryPlatform(platform: string): boolean {
  return STORE_BINARY_PLATFORMS.includes(platform);
}

/** Normalizes a declared surface. Total: whatever doesn't classify ⇒ `unknown`. */
export function normalizePlatform(raw: string): ResolvedPurchasePlatform {
  const v = raw.trim().toLowerCase();
  return isPurchasePlatform(v) ? v : UNKNOWN_PURCHASE_PLATFORM;
}

/** Legal providers on a surface (fail-closed on unknown). */
export function allowedPaymentProviders(platform: string): readonly string[] {
  return isPurchasePlatform(platform)
    ? PLATFORM_ALLOWED_PAYMENT_PROVIDERS[platform]
    : UNKNOWN_PLATFORM_ALLOWED_PROVIDERS;
}

/**
 * **THE clamp.** EFFECTIVE provider on a surface: the configured one if it's legal
 * there, and `none` if not. Monotonic (only restricts), idempotent, and total.
 */
export function clampProviderToPlatform(platform: string, configuredProvider: string): string {
  const provider = configuredProvider.trim().toLowerCase();
  return allowedPaymentProviders(platform).includes(provider) ? provider : PAYMENT_PROVIDER_NONE;
}

/**
 * Purchase surface detection.
 *
 * The nuance that's most often gotten wrong: **Chrome on an Android phone is NOT the
 * Android binary.** Play policy applies to the APK distributed by Play, not to
 * mobile web. That's why the first thing checked is whether we're inside a
 * native runtime; only then does the operating system decide the surface.
 *
 * Fail-closed: a binary on an OS we don't know how to classify is `unknown`.
 */
export function detectPurchasePlatform(input: {
  /** Are we running inside the binary (Tauri) or in a browser? */
  isNativeRuntime: boolean;
  os: PlatformOS;
}): ResolvedPurchasePlatform {
  if (!input.isNativeRuntime) {
    return "web";
  }
  switch (input.os) {
    case "android": {
      return "android";
    }
    case "ios": {
      return "ios";
    }
    case "macos":
    case "windows":
    case "linux": {
      return "desktop";
    }
    default: {
      return UNKNOWN_PURCHASE_PLATFORM;
    }
  }
}

/** Runtime sugar: the native flag is injected because it lives in the app. */
export function currentPurchasePlatformFor(isNativeRuntime: boolean): ResolvedPurchasePlatform {
  return detectPurchasePlatform({ isNativeRuntime, os: getPlatformOS() });
}

/** Why the surface ended up the way it did (mirror of the Rust enum). */
export type PurchaseGateReason =
  | "offered"
  | "monetization-disabled"
  | "payments-disabled"
  | "unknown-platform"
  | "provider-not-allowed";

export interface PurchaseSurface {
  platform: ResolvedPurchasePlatform;
  /** EFFECTIVE provider after the non-negotiable clamp. */
  provider: string;
  offersPurchase: boolean;
  reason: PurchaseGateReason;
}

/** Purchase config as seen by the resolver (subset of `MonetizationConfig`). */
export interface PurchaseSurfaceRequest {
  /**
   * Surface. It's a `string` on purpose: it can come from detection, from a
   * config, or from a server response, and the resolver has to be TOTAL.
   */
  platform: string;
  enabled: boolean;
  paymentsEnabled: boolean;
  provider: string;
}

/**
 * Composes config + surface and returns the effective purchase.
 *
 * Invariant that holds up the rest: **the returned `provider` is always in
 * [`allowedPaymentProviders`] of the returned platform**, whether the purchase is offered
 * or not.
 */
export function resolvePurchaseSurface(request: PurchaseSurfaceRequest): PurchaseSurface {
  const platform = normalizePlatform(request.platform);
  const configured = request.provider.trim().toLowerCase();
  const provider = clampProviderToPlatform(platform, configured);

  const gated = (reason: PurchaseGateReason): PurchaseSurface => ({
    platform,
    provider,
    offersPurchase: false,
    reason,
  });

  if (!request.enabled) {
    return gated("monetization-disabled");
  }
  if (!request.paymentsEnabled) {
    return gated("payments-disabled");
  }
  if (platform === UNKNOWN_PURCHASE_PLATFORM) {
    return gated("unknown-platform");
  }
  if (provider !== configured) {
    return gated("provider-not-allowed");
  }
  return { platform, provider, offersPurchase: true, reason: "offered" };
}
